<!--
  canonical product editor EditorPagehead — top-of-editor page head.

  Plan: planning/design-handoffs/handoff_2026-06-13/2026-06-13-product-editor/
        design/Product Editor - Standalone.html (canonical mockup).
  Sprint card: https://trello.com/c/hYlwyKYT

  Layout:
    +---------------------------------------------------------------+
    | <Store Name> / Products / <Current>         [pill] o Unsaved  |
    | [<] EDITING PRODUCT                          Revert  Save     |
    |     <Store Name> > <Product Name H1>                          |
    +---------------------------------------------------------------+

  Notes:
    - The header shows the REAL owning-store name followed by the
      product name ("{store} > {product}"). Both are prominent and
      readable (not the tiny breadcrumb font). The store name is a
      read-only denormalized attribute (`store_name`) surfaced by the
      CampaignProduct SAFRS serializer (stores.name via store_id) and
      read through the same effective-attribute getter as
      `product_name`. This replaced the earlier interim pagehead that
      hardcoded placeholder crumbs ("Acme" + "Fall 2026") that lied
      about the store on every product regardless of which store it
      belonged to.
    - `hide_from_storefront` is wired to the real
      `campaign_product.hide_from_storefront` attribute via the
      productEditor store. The pill mirrors the gallery eye-toggle
      (ProductCard.vue) -- both POST to
      /campaign_product/toggleHideFromStorefront and read the same
      attribute. The icon + label derive from the store-effective
      attribute so the pill stays in lockstep with server state.
    - Uses material-icons ligature names per
      project_vuetify-icon-set-is-md-not-mdi.md (defaultSet = 'md').
-->
<template>
    <header data-testid="editor-pagehead-root" class="pagehead-root">
        <div class="pagehead-left">
            <nav
                data-testid="editor-pagehead-breadcrumb"
                class="pagehead-breadcrumb"
                aria-label="Breadcrumb"
            >
                <span
                    data-testid="editor-pagehead-crumb-store"
                    class="crumb"
                >{{ storeName }}</span>
                <span class="crumb-sep" aria-hidden="true">/</span>
                <span
                    data-testid="editor-pagehead-crumb-section"
                    class="crumb"
                >Products</span>
                <span class="crumb-sep" aria-hidden="true">/</span>
                <span
                    data-testid="editor-pagehead-crumb-current"
                    class="crumb crumb-current"
                >{{ productName }}</span>
            </nav>
            <div class="pagehead-title-row">
                <button
                    data-testid="editor-pagehead-back-button"
                    class="pagehead-back"
                    type="button"
                    aria-label="Back"
                    @click="onBack"
                >
                    <span class="material-icons" aria-hidden="true"
                        >arrow_back</span
                    >
                </button>
                <div class="pagehead-title-stack">
                    <span
                        data-testid="editor-pagehead-eyebrow"
                        class="pagehead-eyebrow"
                        >EDITING PRODUCT</span
                    >
                    <h1
                        data-testid="editor-pagehead-product-name"
                        class="pagehead-h1"
                    >
                        <span
                            v-if="storeName"
                            data-testid="editor-pagehead-store-name"
                            class="pagehead-store-name"
                            >{{ storeName }}</span
                        ><span
                            v-if="storeName"
                            class="pagehead-title-sep"
                            aria-hidden="true"
                            >&nbsp;&rsaquo;&nbsp;</span
                        ><span
                            data-testid="editor-pagehead-product-name-text"
                            class="pagehead-product-name"
                            >{{ productName }}</span
                        >
                    </h1>
                </div>
            </div>
        </div>

        <div class="pagehead-right">
            <!--
              Pricing mode (2026-09-29, Jake): named on every tab, not only
              Pricing, so nobody edits a fixed-price product believing the
              store tables set its price (or vice versa).
            -->
            <PricingModeBadge
                :mode="pricingMode"
                testid="editor-pagehead-pricing-mode"
            />
            <!--
              Phase 1 wireup (workstream #2): the pill now reads the
              real `campaign_product.hide_from_storefront` attribute via
              the productEditor store and round-trips through the same
              backend endpoint the gallery eye-toggle (ProductCard.vue)
              has used since Day 6.5
              (POST /campaign_product/toggleHideFromStorefront). The
              icon + label are derived from the store-effective value
              so the pill stays in lockstep with server state -- never
              from a local ref that could lie to the user about the
              persisted state. The earlier "disabled + tooltip" stub
              (Day 2 gate round-1 security P2 CWE-1021 mitigation) is
              gone now that the real attribute drives the state.
            -->
            <button
                data-testid="editor-pagehead-hide-toggle"
                class="pagehead-pill"
                type="button"
                :aria-pressed="hiddenFromStorefront ? 'true' : 'false'"
                :disabled="hideToggleInFlight"
                @click="toggleHide"
            >
                <span class="material-icons pill-mi" aria-hidden="true">{{
                    hiddenFromStorefront ? "visibility_off" : "visibility"
                }}</span>
                <span class="pill-label">{{
                    hiddenFromStorefront
                        ? "Hidden from storefront"
                        : "Hide from storefront"
                }}</span>
            </button>

            <!--
              Field-only autosave (MVP, per Jake 2026-07-09; full autosave
              deferred post-MVP: https://trello.com/c/3v7gvA8L). Scalar field
              edits autosave; the subtle indicator below mirrors the store
              editor's dirty -> saving -> saved idiom. The Save + Revert
              buttons are STRUCTURAL-only -- they light up only for unsaved
              structural changes (decorations / variants / skip-colors /
              personalization / reorders / deletes), which the store surfaces
              as `hasUnsavedStructuralChanges`. See the productEditor store's
              field-only-autosave block. Design doc:
              docs/planning/2026-07-10-product-editor-autosave-proposal.md.
            -->
            <div
                v-if="fieldAutoSaveLabel"
                data-testid="product-editor-autosave-state"
                class="pagehead-autosave"
                :class="{ 'pagehead-autosave-error': isAutoSaveError }"
                aria-live="polite"
            >
                <span class="autosave-label">{{ fieldAutoSaveLabel }}</span>
            </div>

            <!--
              Settle sentinel for automation: present exactly when no scalar
              edit is unsaved ('idle' or 'saved'), absent while one is dirty,
              in flight, or failed. A scenario waits on it before reading the
              DB. The badge above cannot serve: its testid renders for
              dirty/saving too, and 'Saved' itself lingers only 1.5s, which a
              polling runner can miss. Correct only because the store enters
              'saved' solely when nothing is left dirty. Not visible.
            -->
            <span
                v-if="isAutoSaveSettled"
                data-testid="product-editor-autosave-settled"
                hidden
            />

            <div
                v-if="store.hasUnsavedStructuralChanges"
                data-testid="editor-pagehead-unsaved-indicator"
                class="pagehead-unsaved"
            >
                <span class="unsaved-dot" aria-hidden="true"></span>
                <span class="unsaved-label">Unsaved changes</span>
            </div>

            <!--
              Revert + Save product are hidden on the Pricing tab (Jake,
              2026-10-09, GH #4290). Every Pricing edit is a scalar
              updateField that autosaves, so the buttons were always
              disabled there. They stay on the tabs that stage structural
              changes.
            -->
            <button
                v-if="showSaveControls"
                data-testid="editor-pagehead-revert-button"
                class="pagehead-text-btn"
                type="button"
                :disabled="
                    !store.hasUnsavedStructuralChanges || store.saveInFlight
                "
                @click="onRevert"
            >Revert</button>

            <button
                v-if="showSaveControls"
                data-testid="editor-pagehead-save-button"
                class="pagehead-primary-btn"
                type="button"
                :disabled="
                    !store.hasUnsavedStructuralChanges || store.saveInFlight
                "
                @click="onSave"
            >Save product</button>
        </div>
    </header>
</template>

<script setup>
import { describeError } from "@/utils/safeErrorLog";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useProductEditorStore } from "@/stores/productEditor";
import {
    LEFT_RAIL_ITEMS,
    useProductEditorUiState,
} from "@/stores/productEditorUiState";
import axios from "@/axios";
import PricingModeBadge from "@/pages/distributor/store-editor/StoreProducts/PricingModeBadge.vue";

const store = useProductEditorStore();
const uiState = useProductEditorUiState();
const router = useRouter();

const showSaveControls = computed(
    () => uiState.selectedLeftRailItem !== LEFT_RAIL_ITEMS.PRICING,
);
// The app's single existing notification mechanism (frontend/src/store/notification.js,
// Vuex). Named vuexStore (not store) to avoid colliding with the Pinia
// productEditor store above -- matches the naming convention already used
// in Stage.vue for the same coexistence.
const vuexStore = useStore();

// Read product name through the effective-attribute getter so it
// reflects the user's in-flight edit (matches the listing panel).
const productName = computed(() => {
    const name = store.getEffectiveAttribute?.("product_name");
    return name && String(name).length > 0 ? name : "Untitled product";
});

// Read the owning store's human name through the same effective-attribute
// getter as productName. `store_name` is a read-only denormalized
// attribute surfaced by the CampaignProduct SAFRS serializer
// (stores.name via store_id). Falls back to an empty string (never the
// old hardcoded "Acme" placeholder) so the header degrades gracefully
// during the pre-load window or on an orphaned CP with no owning store;
// the template hides the store-name element + separator when empty.
const pricingMode = computed(
    () => store.getEffectiveAttribute?.("pricing_mode") ?? null,
);

const storeName = computed(() => {
    const name = store.getEffectiveAttribute?.("store_name");
    return name && String(name).length > 0 ? String(name) : "";
});

// Hide-from-storefront wireup (workstream #2):
//   - Derive the pill's icon + label from the store's effective
//     attribute view of `hide_from_storefront`. This matches the
//     ProductCard.vue gallery eye-toggle's source-of-truth pattern
//     (the prop drives the icon; never a local ref). Falling back to
//     `false` keeps the pill in the visible state during the
//     pre-load window before serverState lands.
//   - Clicking POSTs the same endpoint the gallery eye-toggle hits.
//     The backend toggles the row, so we mirror with `!current` on
//     success rather than reading the response body. On failure we
//     leave the bound attribute untouched so the pill keeps reflecting
//     the persisted state.
const hiddenFromStorefront = computed(() => {
    const value = store.getEffectiveAttribute?.("hide_from_storefront");
    return Boolean(value);
});

// Field-only autosave indicator (MVP). Mirrors the store editor's
// dirty -> saving -> saved idiom as a subtle text badge. Hidden (empty
// label) when idle. The 'error' state keeps the label visible so a failed
// autosave is not silent; the store keeps the dirty Map and retries on the
// next debounce.
const fieldAutoSaveLabel = computed(() => {
    switch (store.fieldAutoSaveState) {
        case "dirty":
            return "Unsaved changes";
        case "saving":
            return "Saving...";
        case "saved":
            return "Saved";
        case "error":
            return "Could not save - retrying";
        default:
            return "";
    }
});

const isAutoSaveSettled = computed(
    () =>
        store.fieldAutoSaveState === "idle"
        || store.fieldAutoSaveState === "saved",
);

const isAutoSaveError = computed(
    () => store.fieldAutoSaveState === "error",
);

const hideToggleInFlight = ref(false);

async function toggleHide() {
    // Guard against the pre-load window where the user could click
    // before serverState (and therefore campaignProductId) has landed.
    // Without this, axios would POST {campaign_product_id: null} and
    // the backend would 404 on the ownership check.
    const cpId = store.campaignProductId;
    if (cpId == null) return;
    if (hideToggleInFlight.value) return;

    const next = !hiddenFromStorefront.value;
    hideToggleInFlight.value = true;
    try {
        await axios.post("/campaign_product/toggleHideFromStorefront", {
            campaign_product_id: cpId,
        });
        // Land the new value on serverState so the derived label flips
        // without forcing a refetch round-trip. Mirrors the
        // emit-up-then-mutate pattern from the gallery (where
        // StoreProducts/index.vue writes into the jsonapi-vuex proxy),
        // adapted for the productEditor Pinia store.
        if (typeof store.setHideFromStorefront === "function") {
            store.setHideFromStorefront(next);
        }
    } catch (err) {
        // The bound attribute is NOT mutated, so the pill keeps showing the
        // persisted state. Say why: the server refuses showing a product
        // whose placement location isn't valid for its SKU (card #545), and
        // a silent no-op read as a broken button. raiseError shows a 4xx
        // body's own message and a generic one otherwise.
        // eslint-disable-next-line no-console
        console.warn("[EditorPagehead] toggleHide failed", describeError(err));
        vuexStore.dispatch("raiseError", err);
    } finally {
        hideToggleInFlight.value = false;
    }
}

// Read store_id from the loaded campaign_product. SAFRS serializes the
// canonical FK column (`store_id` post PR-5b rename) as an attribute on
// the resource. A `campaign_id` fallback is kept below as defensive
// belt-and-suspenders for any in-flight cached / stale CP payloads that
// still carry the pre-rename key. Falls back to null until the
// server-state load completes; the back button gates on this so an
// in-flight load doesn't navigate to /undefined.
const owningStoreId = computed(() => {
    const attrs = store.serverState?.data?.attributes;
    if (!attrs) return null;
    return attrs.store_id ?? attrs.campaign_id ?? null;
});

function onBack() {
    // Navigate to the owning store editor's Products section -- the
    // section the user was in when they opened this product. The store
    // editor route is `/stores/:id/edit/:section?`; omitting :section
    // lands on the Review & Launch landing instead, which is the wrong
    // return target for the product editor back arrow. If the store_id
    // hasn't resolved yet (rare race: user hammers the button before
    // serverState lands), do nothing rather than push to a broken URL.
    const id = owningStoreId.value;
    if (id == null) return;
    router.push({
        name: "store_edit",
        params: { id: String(id), section: "products" },
    });
}

function onRevert() {
    if (typeof store.revert === "function") {
        store.revert();
    }
}

// Issue #2446 root-cause fix: the editor previously had NO save-success
// channel at all. The Save button's disabled state was the only signal,
// and once a save round-tripped, the only feedback a distributor got was
// whatever the Stage preview tile happened to render -- which reads as
// "my work didn't save" when an unrelated Artifi preview render fails
// (previews are decoupled from save by design, see
// _maybe_schedule_eager_preview_render on the backend). Fire a transient
// success toast through the app's EXISTING notification mechanism
// (Vuex store/notification.js raiseSuccess -- the same action
// ProductAddEdit.vue and StoreAddEdit.vue already use for save
// confirmations) on a genuinely clean save only.
//
// store.save() never throws for a partial (Group A/B) failure -- it
// collects failures into saveErrors[] instead (see productEditor.js
// save()). So "clean" is judged by re-reading saveErrors/saveError AFTER
// the await, not by a try/catch alone. A promise rejection (e.g. the
// early-return guard when campaignProductId is missing, or a future
// throwing implementation) is also treated as a failure. Either way, no
// toast fires -- silence rather than a false "Product saved".
// Colors the supplier no longer sells (Jake, 2026-10-07): a save that drops
// them raises store.unsellableColorNotice; show it through the same
// notification snackbar. Keyed on the notice id so a repeat message fires.
watch(
    () => store.unsellableColorNotice?.id,
    () => {
        const notice = store.unsellableColorNotice;
        if (notice?.message) vuexStore.dispatch("raiseInfo", notice.message);
    },
);

async function onSave() {
    if (typeof store.save !== "function") return;
    const noticeBefore = store.unsellableColorNotice?.id;
    try {
        await store.save();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[EditorPagehead] save failed", describeError(err));
        return;
    }
    const hasErrors
        = (Array.isArray(store.saveErrors) && store.saveErrors.length > 0)
        || store.saveError != null;
    if (hasErrors) return;
    // One snackbar: a drop announced by this save is the news, and it
    // already says the product was saved without those colors.
    if (store.unsellableColorNotice?.id !== noticeBefore) return;
    vuexStore.dispatch("raiseSuccess", "Product saved");
}
</script>

<style scoped>
.pagehead-root {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 24px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
    background: var(--tf-surface, #ffffff);
}

.pagehead-left {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.pagehead-breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 1.2;
    color: var(--tf-text-muted, #6b6b6b);
}

.crumb {
    color: var(--tf-text-muted, #6b6b6b);
}

.crumb-current {
    color: var(--tf-text-default, #1f1f1f);
}

.crumb-sep {
    color: var(--tf-text-muted, #b0b0b0);
}

.crumb-sep-dot {
    font-weight: 700;
}

.pagehead-title-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
}

.pagehead-back {
    appearance: none;
    background: transparent;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--tf-text-default, #1f1f1f);
    cursor: pointer;
    margin-top: 4px;
}

.pagehead-back:hover {
    background: var(--tf-surface-hover, rgba(0, 0, 0, 0.04));
}

.pagehead-back .material-icons {
    font-size: 20px;
}

.pagehead-title-stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.pagehead-eyebrow {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--tf-text-muted, #6b6b6b);
}

.pagehead-h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.2;
    color: var(--tf-text-default, #1f1f1f);
}

/* Store name leads the H1 in a slightly de-emphasized weight/color so the
   product name reads as the primary subject while the store stays clearly
   readable (per Jake: both visible, neither tiny). The chevron separator
   inherits the muted store color. */
.pagehead-store-name {
    font-weight: 500;
    color: var(--tf-text-muted, #6b6b6b);
}

.pagehead-title-sep {
    color: var(--tf-text-muted, #b0b0b0);
    font-weight: 400;
}

.pagehead-product-name {
    color: var(--tf-text-default, #1f1f1f);
}

.pagehead-right {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

.pagehead-pill {
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 12px;
    border-radius: 14px;
    background: var(--tf-surface-soft, #f5f5f5);
    border: 1px solid var(--tf-border, #e0e0e0);
    color: var(--tf-text-default, #1f1f1f);
    font-size: 12px;
    cursor: pointer;
}

.pill-mi {
    font-size: 16px;
}

.pagehead-autosave {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: var(--tf-text-muted, #6b6b6b);
    font-style: italic;
    white-space: nowrap;
}

.pagehead-autosave-error {
    color: var(--tf-danger, #c62828);
    font-style: normal;
}

.pagehead-unsaved {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--tf-text-muted, #6b6b6b);
}

.unsaved-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f5b400;
}

.pagehead-text-btn {
    appearance: none;
    background: transparent;
    border: none;
    color: var(--tf-text-muted, #6b6b6b);
    font-size: 13px;
    cursor: pointer;
    padding: 6px 8px;
}

.pagehead-text-btn:not(:disabled):hover {
    color: var(--tf-text-default, #1f1f1f);
}

.pagehead-text-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagehead-primary-btn {
    appearance: none;
    height: 36px;
    padding: 0 16px;
    border-radius: 4px;
    border: none;
    background: var(--tf-brand-primary, #1f6feb);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.pagehead-primary-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
